import { useState } from 'react';
import { Sparkle, X } from '@phosphor-icons/react';
import ReactMarkdown from 'react-markdown';
import { getModuleColor } from '../config/modules';
import {
  executeAssistantAction,
  pedirSiguientePaso,
  resultadoSiguientePaso,
  type SiguientePaso as Paso,
} from '../services/api';
import styles from './SiguientePaso.module.css';

/**
 * «¿Qué harías aquí?» (paso 51b): el agente lee el ticket y propone el
 * siguiente paso. Si es asignar o cambiar el estado, viene como tarjeta y se
 * aplica con el ejecutor de siempre (`/admin/assistant/execute`), con los
 * permisos de quien pulsa.
 *
 * Lo que hace el técnico con la propuesta —aplicarla o descartarla— queda
 * apuntado (`core.ia_siguiente_paso`): es el «nivel de aciertos» que Jose
 * quiere medir. Va con el color de la IA, no con la tinta (paso 47).
 */
export function SiguientePaso({
  token,
  incidenciaId,
  onCambio,
}: {
  token: string;
  incidenciaId: string;
  /** Tras aplicar una acción: el ticket ha cambiado y hay que releerlo. */
  onCambio: () => void;
}) {
  const ai = getModuleColor('asistente');
  const [paso, setPaso] = useState<Paso | null>(null);
  const [pensando, setPensando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hechas, setHechas] = useState<Record<number, string>>({});
  const [aplicando, setAplicando] = useState<number | null>(null);

  const pedir = async () => {
    setPensando(true);
    setError(null);
    setHechas({});
    try {
      setPaso(await pedirSiguientePaso(token, incidenciaId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPensando(false);
    }
  };

  const aplicar = async (i: number) => {
    if (!paso) return;
    const card = paso.acciones[i]!;
    setAplicando(i);
    setError(null);
    try {
      const { resultText } = await executeAssistantAction(token, card.actionType, card.params);
      setHechas((h) => ({ ...h, [i]: resultText }));
      resultadoSiguientePaso(token, paso.id, 'aplicada', card.actionType);
      onCambio();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAplicando(null);
    }
  };

  const descartar = () => {
    if (paso && Object.keys(hechas).length === 0) resultadoSiguientePaso(token, paso.id, 'descartada');
    setPaso(null);
    setError(null);
  };

  const vars = { '--ai': ai.accent, '--ai-bg': ai.bg } as React.CSSProperties;

  if (!paso) {
    return (
      <div className={styles.wrap} style={vars}>
        <button type="button" className={styles.boton} onClick={pedir} disabled={pensando}>
          <Sparkle size={14} weight="fill" /> {pensando ? 'Pensando…' : '¿Qué harías aquí?'}
        </button>
        {error && <span className={styles.error} role="alert">{error}</span>}
      </div>
    );
  }

  return (
    <section className={styles.panel} style={vars} aria-label="Lo que haría el asistente">
      <div className={styles.cabecera}>
        <span className={styles.titulo}><Sparkle size={13} weight="fill" /> Lo que haría el asistente</span>
        <button type="button" className={styles.cerrar} onClick={descartar} aria-label="Descartar la propuesta" title="Descartar">
          <X size={14} weight="bold" />
        </button>
      </div>
      <div className={styles.texto}><ReactMarkdown>{paso.recomendacion}</ReactMarkdown></div>
      {paso.acciones.map((a, i) => (
        <div key={i} className={styles.accion}>
          <div className={styles.accionTxt}>
            <span className={styles.accionLabel}>{a.label}</span>
            {a.summary && <span className={styles.accionSummary}>{a.summary}</span>}
          </div>
          {hechas[i] ? (
            <span className={styles.hecha}>{hechas[i]}</span>
          ) : (
            <button type="button" className={styles.aplicar} onClick={() => aplicar(i)} disabled={aplicando !== null}>
              {aplicando === i ? 'Aplicando…' : 'Aplicar'}
            </button>
          )}
        </div>
      ))}
      {error && <span className={styles.error} role="alert">{error}</span>}
      <div className={styles.pie}>
        <button type="button" className={styles.otra} onClick={pedir} disabled={pensando}>
          {pensando ? 'Pensando…' : 'Preguntar otra vez'}
        </button>
        {Object.keys(hechas).length === 0 && (
          <button type="button" className={styles.otra} onClick={descartar}>No me sirve</button>
        )}
      </div>
    </section>
  );
}
